import { useState } from 'react';

import { SmartApplyApiError } from '../lib/api-client.js';
import { useAuth } from '../lib/auth-context.js';
import { useRoute } from '../lib/route.js';
import { AuthLayout } from '../components/auth/AuthLayout.js';
import { PasswordInput } from '../components/auth/PasswordInput.js';
import { PasswordStrength } from '../components/auth/PasswordStrength.js';
import { OAuthButtons } from '../components/auth/OAuthButtons.js';

export function RegisterRoute() {
  const { register } = useAuth();
  const { navigate } = useRoute();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await register({ email, password, displayName });
      navigate('/');
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao criar conta.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Criar conta"
      subtitle="Você precisará de uma API key de IA (Anthropic, OpenAI, Google ou OpenRouter) para rodar análises. A chave fica só em sessão."
      badge="cadastro"
      side={
        <div className="sticky top-12 space-y-6 border-l border-[var(--border-subtle)] pl-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              já tem conta?
            </p>
            <a
              href="#/login"
              className="mt-2 inline-block font-serif text-base text-[var(--ink-primary)] underline-offset-4 hover:underline"
            >
              Entrar →
            </a>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              o que persistimos
            </p>
            <ul className="mt-2 space-y-1 font-sans text-xs text-[var(--ink-secondary)]">
              <li>→ E-mail e nome</li>
              <li>→ Hash Argon2id da senha</li>
              <li>→ Sessões revogáveis</li>
              <li className="text-[var(--ink-tertiary)]">
                ✗ PDF do LinkedIn (efêmero)
              </li>
              <li className="text-[var(--ink-tertiary)]">✗ API key de IA</li>
            </ul>
          </div>
          <p className="border-t border-[var(--border-subtle)] pt-4 font-sans text-xs italic leading-relaxed text-[var(--ink-tertiary)]">
            Ambiente local de desenvolvimento. Em produção, e-mails de
            verificação serão enviados via Resend.
          </p>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="card-paper rounded-sm px-7 py-7" style={{ borderLeft: '3px solid var(--accent-saffron)' }}>
        <div className="space-y-5">
          <div>
            <label
              htmlFor="displayName"
              className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
            >
              Como você quer ser chamado
            </label>
            <input
              id="displayName"
              type="text"
              autoComplete="name"
              required
              minLength={2}
              maxLength={80}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
            >
              E-mail
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
            />
          </div>

          <div>
            <PasswordInput
              id="password"
              label="Senha"
              autoComplete="new-password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint="Mínimo 12 chars com 3 das 4 classes (a-z, A-Z, 0-9, especial)."
            />
            {password && <PasswordStrength value={password} />}
          </div>

          {error && (
            <div className="rounded-sm border border-[var(--accent-crimson-border)] bg-[var(--accent-crimson-bg)] px-4 py-2.5">
              <p className="font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email || !password || !displayName}
            className="rounded-sm bg-[var(--ink-primary)] px-5 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {loading ? 'Criando conta...' : 'Criar conta'}
          </button>
        </div>

        <div className="mt-6">
          <OAuthButtons />
        </div>
      </form>
    </AuthLayout>
  );
}
